import { clarity } from 'clarity-js'
import isSSR from '@/_base/isSSR'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'

// Endpoints de ingestão/telemetria do Clarity — os mesmos que o loader gerenciado usa.
// clarity-js@0.8.66 só transmite quando `upload` é string (build/clarity.js:4523);
// sem isto o engine coleta/mascara mas NUNCA envia (causa raiz do smoke V1 — ADR-0010).
const CLARITY_UPLOAD_ENDPOINT = 'https://e.clarity.ms/collect'
const CLARITY_REPORT_ENDPOINT = 'https://m.clarity.ms/report'

// Sentinelas de idempotência no escopo do módulo: initClarity/identifyUser são
// chamados a cada navegação (router.afterEach), então só agem uma vez por sessão.
// stopClarity (no logout) reseta ambas, permitindo nova sessão em re-login na mesma aba.
let alreadyStarted = false
let alreadyIdentified = false

// Pattern B (error-handling.md): telemetria é não-crítica e NUNCA pode quebrar
// navegação ou logout. Toda chamada ao SDK é envolvida; falha do Clarity é engolida.
function safe(action: () => void): void {
  try {
    action()
  } catch {
    // noop — falha de analytics não propaga
  }
}

// Stub de opt-out (LGPD — base legal de legítimo interesse, ADR-0008). v1 sempre
// retorna false (analytics habilitado por padrão).
// TODO(clarity-opt-out): ligar à preferência do usuário / consent store (fast-follow).
function isOptedOut(): boolean {
  return false
}

// Inicia a captura do Clarity. Idempotente (seguro chamar a cada navegação).
// Mascaramento forçado em código (não burlável por dashboard):
//   - content:false mascara texto E imagem (src→vazio, blob→'blob:'); canvas nunca é capturado;
//   - drop scrubba o VALOR de query-params que podem carregar PII de busca livre
//     (q/search/names = nome/CRM/CPF digitados; tab = defense-in-depth).
export function initClarity(): void {
  if (isSSR) return
  if (isOptedOut()) return

  const { flags } = useFeatureFlags()
  if (!flags.enabledClarity) return

  const projectId = process.env.VUE_APP_CLARITY_PROJECT_ID
  if (!projectId) return

  if (alreadyStarted) return

  // Marca antes de chamar o SDK: init é best-effort e roda no máximo uma vez por
  // sessão (falha do start() não dispara retry a cada navegação).
  alreadyStarted = true

  // Política do `drop`: scrubba (valor → "dropped" na URL CAPTURADA pelo Clarity,
  // SEM afetar a URL real ou o app) TODO query-param que possa carregar texto livre
  // ou identificador pessoal/de domínio (nome/CRM/CPF/CNPJ/e-mail). Custo zero por
  // entrada → na dúvida, incluir. A app serializa busca de médico/unidade na URL via
  // router.replace({ query }) (useFilterStation, useDoctorStore, useHealthUnitStore).
  // Completude desta lista é GATE pré-go-live (DPO/smoke), não follow-up opcional.
  safe(() =>
    clarity.start({
      projectId,
      content: false,
      // upload (string) liga o transporte; não há callback que reexponha payload, então o
      // masking fail-closed (content:false) fica intacto. report = telemetria de erro do SDK.
      upload: CLARITY_UPLOAD_ENDPOINT,
      report: CLARITY_REPORT_ENDPOINT,
      drop: [
        // texto livre (nome/CRM/CPF digitados)
        'q',
        'search',
        'text',
        'names',
        'doctorName',
        // identificadores de domínio
        'crms',
        'crmsUf',
        'crmUFs',
        'cnpjs',
        // defense-in-depth (ambíguos)
        'tab',
        'g'
      ]
    })
  )
}

// Identifica o usuário-empresa. O Clarity hasheia o userId (SHA-256) como id estável.
// NUNCA enviar CRM/CPF/nome/PII de terceiros. `email` (opcional) vira friendlyName visível
// no dashboard — sem caller em v1 (sourcing do e-mail no front é fast-follow).
// Tag `company_id` lida do localStorage (CSV) como string[] para filtro por empresa.
export function identifyUser(userId: number, email?: string): void {
  if (!alreadyStarted || alreadyIdentified) return
  if (!userId) return

  // Marca antes do SDK: identify roda no máximo uma vez por sessão (sem retry storm).
  // company_id é best-effort no mesmo bloco.
  alreadyIdentified = true

  safe(() => {
    clarity.identify(String(userId), undefined, undefined, email)

    const companies = localStorage.getItem('@ME:companies_id')
    if (companies) clarity.set('company_id', companies.split(','))
  })
}

// Adiciona o nome limpo da rota como tag (o Clarity já captura window.location).
export function trackPage(routeName: string | symbol | null | undefined, routePath: string): void {
  if (!alreadyStarted) return

  const pageName = typeof routeName === 'string' ? routeName : routePath
  safe(() => clarity.set('page_name', pageName))
}

// Tag custom da sessão (ex.: `dashboard_v3`, dec. 72 do dashboard-mobile-v2): filtra as
// gravações por recurso ligado. No-op antes do start; falha do SDK é engolida.
export function setTag(key: string, value: string | string[]): void {
  if (!alreadyStarted) return

  safe(() => clarity.set(key, value))
}

// Gancho extensível para eventos custom (convenção de nome: `area.action`).
// Sem call site em v1 — exportado para fast-follows.
export function trackEvent(name: string, value = ''): void {
  if (!alreadyStarted) return

  safe(() => clarity.event(name, value))
}

// Encerra a captura e reseta as sentinelas. Roda no logout de TODOS os usuários
// (médicos, flag-off, SSR) — por isso o guard `!alreadyStarted` é obrigatório.
export function stopClarity(): void {
  if (isSSR || !alreadyStarted) return

  safe(() => clarity.stop())
  alreadyStarted = false
  alreadyIdentified = false
}
